import type { ExceptionsReport } from '@morales/shared';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../api';
import { PeriodBar, presets, usePeriod } from '../components/PeriodBar';
import { Sheet, type SheetColumn } from '../components/Sheet';
import { byNumber, byText, useSort } from '../components/useSort';
import { Empty, PageHead, QueryView } from '../components/ui';
import { addDays, formatDate, formatMoney, formatQty } from '../format';
import { useDesktop } from '../hooks';
import { useLocale } from '../i18n';
import { qk } from '../queryClient';

const RANGES = presets('d7', 'd30', 'd90');

type Line = ExceptionsReport['pricing'][number];
type SortKey = 'date' | 'no' | 'customer' | 'item' | 'qty' | 'price' | 'gap';

/** The exceptions of a period (owner/office): what went against the rules, with the money it cost. */
export function ExceptionsPage() {
  const desktop = useDesktop();
  return desktop ? <ExceptionsSheet /> : <ExceptionsList />;
}

/** The exception report on a desk: the totals in a strip, every pricing exception on the sheet, credits and shrink beside. */
function ExceptionsSheet() {
  const { t, intlLocale } = useLocale();
  const navigate = useNavigate();
  const period = usePeriod(RANGES);
  const query = useQuery({ queryKey: qk.exceptions(period.from, period.to), queryFn: ({ signal }) => api.exceptions({ from: period.from, to: period.to }, signal), placeholderData: keepPreviousData });
  const sorters = useMemo(
    () => ({
      date: byText<Line>((l) => l.date),
      no: byNumber<Line>((l) => l.invoiceNo),
      customer: byText<Line>((l) => l.customerName),
      item: byText<Line>((l) => l.itemName),
      qty: byNumber<Line>((l) => l.qty),
      price: byNumber<Line>((l) => l.unitPrice),
      gap: byNumber<Line>((l) => l.gap),
    }),
    [],
  );
  const { sorted, head } = useSort<Line, SortKey>(sorters, { key: 'gap', dir: -1 }, ['date', 'no', 'qty', 'price', 'gap']);
  const money = (v: number | null) => (v === null ? '' : formatMoney(v, intlLocale));
  const columns: SheetColumn<Line>[] = [
    { key: 'date', header: head('date', t('docDate')), label: t('docDate'), width: '104px', render: (l) => formatDate(l.date, intlLocale, 'date') },
    { key: 'no', header: head('no', t('invoice')), label: t('invoice'), width: '80px', align: 'right', render: (l) => <Link to={`/invoices/${l.invoiceId}`}>{l.invoiceNo}</Link> },
    { key: 'customer', header: head('customer', t('customer')), label: t('customer'), width: '150px', render: (l) => l.customerName },
    { key: 'item', header: head('item', t('itemCol')), label: t('itemCol'), width: '150px', render: (l) => l.itemName },
    { key: 'qty', header: head('qty', t('qtyCol')), label: t('qtyCol'), width: '64px', align: 'right', render: (l) => formatQty(l.qty) },
    { key: 'price', header: head('price', t('priceCol')), label: t('priceCol'), width: '88px', align: 'right', render: (l) => money(l.unitPrice), cellClass: (l) => (l.belowCost ? 'bad' : l.underFloor ? 'warn' : undefined) },
    { key: 'cost', header: t('cost'), width: '84px', align: 'right', render: (l) => money(l.cost) },
    { key: 'floor', header: t('floor'), width: '84px', align: 'right', render: (l) => money(l.floorPrice) },
    { key: 'tier', header: t('tier'), width: '84px', align: 'right', render: (l) => money(l.tierPrice) },
    {
      key: 'flag',
      header: t('flagCol'),
      width: '104px',
      render: (l) =>
        l.belowCost ? <span className="state state-overdue">{t('belowCostBadge')}</span> : l.underFloor ? <span className="state state-overdue">{t('underFloorBadge')}</span> : l.underTier ? <span className="state state-open">{t('underTierBadge')}</span> : '',
    },
    { key: 'gap', header: head('gap', t('leftOnTable')), label: t('leftOnTable'), width: '112px', align: 'right', render: (l) => <strong>{money(l.gap)}</strong> },
  ];

  return (
    <div className="desk-page">
      <header className="desk-head">
        <div>
          <h1>{t('exceptionsTitle')}</h1>
          <p className="muted">{t('exceptionsHint')}</p>
        </div>
      </header>
      <div className="toolbar">
        <PeriodBar presets={RANGES} period={period} />
      </div>
      <QueryView query={query}>
        {(r) => (
          <>
            <dl className="totals-strip" data-testid="exceptions-strip">
              <div className={r.totals.pricingGap > 0 ? 'bad' : undefined}>
                <dt>{t('leftOnTable')}</dt>
                <dd data-testid="pricing-gap">{money(r.totals.pricingGap)}</dd>
              </div>
              <div className={r.totals.belowCostLines > 0 ? 'bad' : undefined}>
                <dt>{t('belowCostLines')}</dt>
                <dd>{r.totals.belowCostLines}</dd>
              </div>
              <div>
                <dt>{t('creditsByReason')}</dt>
                <dd>{money(r.totals.creditsAmount)}</dd>
              </div>
              <div>
                <dt>{t('shrinkByCause')}</dt>
                <dd title={r.totals.shrinkCostPartial ? t('shrinkCostPartial') : undefined}>{r.totals.shrinkCost !== null ? `${money(r.totals.shrinkCost)}${r.totals.shrinkCostPartial ? '+' : ''}` : `${formatQty(r.totals.shrinkPacks)} ${t('packs')}`}</dd>
              </div>
              <div>
                <dt>{t('voidedInvoices')}</dt>
                <dd>{r.voided.count ? `${r.voided.count} · ${money(r.voided.amount)}` : '0'}</dd>
              </div>
              <div>
                <dt>{t('cancelledOrders')}</dt>
                <dd>{r.cancelledOrders}</dd>
              </div>
              <div className={r.unpricedLines > 0 ? 'warn' : undefined}>
                <dt>{t('unpricedLinesLabel')}</dt>
                <dd>{r.unpricedLines}</dd>
              </div>
            </dl>
            <h2>{t('pricingExceptions')}</h2>
            {r.pricing.length === 0 ? (
              <p className="muted pick-first">{t('noResults')}</p>
            ) : (
              <Sheet
                label={t('pricingExceptions')}
                columns={columns}
                rows={sorted(r.pricing)}
                rowKey={(l, i) => `${l.invoiceId}-${l.itemId}-${i}`}
                rowLabel={(l) => `${l.invoiceNo} ${l.itemName}`}
                minWidth="1104px"
                onOpen={(l) => navigate(`/invoices/${l.invoiceId}`)}
                footer={
                  <tr>
                    <td>{t('reportTotal')}</td>
                    <td />
                    <td />
                    <td />
                    <td />
                    <td />
                    <td />
                    <td />
                    <td />
                    <td />
                    <td className="al-right">
                      <strong>{money(r.totals.pricingGap)}</strong>
                    </td>
                  </tr>
                }
              />
            )}
            <div className="two-up">
              <section className="stack-sm">
                <h2>{t('creditsByReason')}</h2>
                <table className="data-table" data-testid="credits">
                  <thead>
                    <tr>
                      <th scope="col">{t('reasonCol')}</th>
                      <th scope="col" className="num">{t('countCol')}</th>
                      <th scope="col" className="num">{t('amount')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {r.credits.map((c) => (
                      <tr key={c.reason}>
                        <td>{t(`reason_${c.reason}` as 'reason_other')}</td>
                        <td className="num">{c.count}</td>
                        <td className="num">{money(c.amount)}</td>
                      </tr>
                    ))}
                    {r.credits.length === 0 ? (
                      <tr>
                        <td colSpan={3} className="muted">{t('noResults')}</td>
                      </tr>
                    ) : null}
                  </tbody>
                </table>
              </section>
              <section className="stack-sm">
                <h2>{t('shrinkByCause')}</h2>
                <table className="data-table" data-testid="shrink">
                  <thead>
                    <tr>
                      <th scope="col">{t('reasonCol')}</th>
                      <th scope="col" className="num">{t('packsCol')}</th>
                      <th scope="col" className="num">{t('cost')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {r.shrink.map((x) => (
                      <tr key={x.reason}>
                        <td>{t(`shrink_${x.reason}` as 'shrink_other')}</td>
                        <td className="num">{formatQty(x.packs)}</td>
                        <td className="num" title={x.partial ? t('shrinkCostPartial') : undefined}>{x.cost === null ? t('noCostOnShrink') : `${money(x.cost)}${x.partial ? '+' : ''}`}</td>
                      </tr>
                    ))}
                    {r.shrink.length === 0 ? (
                      <tr>
                        <td colSpan={3} className="muted">{t('noResults')}</td>
                      </tr>
                    ) : null}
                  </tbody>
                </table>
              </section>
            </div>
          </>
        )}
      </QueryView>
    </div>
  );
}

/** The phone's exception report: the totals card, then the lists. */
function ExceptionsList() {
  const { t, intlLocale } = useLocale();
  // the same period as the desk (?from=&to=), so a linked period reads the same on a phone
  const { from, to, today, setRange } = usePeriod(RANGES);
  const query = useQuery({ queryKey: qk.exceptions(from, to), queryFn: ({ signal }) => api.exceptions({ from, to }, signal) });

  return (
    <div className="stack">
      <PageHead title={t('exceptionsTitle')} back="/" />
      <p className="muted">{t('exceptionsHint')}</p>
      <div className="chips" role="group" aria-label={t('period')}>
        {RANGES.map((r) => {
          const start = addDays(today, -r.days);
          return (
            <button key={r.key} type="button" className="chip" aria-pressed={from === start && to === today} onClick={() => setRange({ from: start, to: '' })}>
              {t(`range_${r.key}`)}
            </button>
          );
        })}
      </div>
      <QueryView query={query}>
        {(r) => (
          <>
            <section className="card">
              <h2>{t('total')}</h2>
              <dl className="facts plain">
                <div className="fact">
                  <dt>{t('leftOnTable')}</dt>
                  <dd className="money owed" data-testid="pricing-gap">
                    {formatMoney(r.totals.pricingGap, intlLocale)}
                  </dd>
                </div>
                <div className="fact">
                  <dt>{t('belowCostLines')}</dt>
                  <dd>{r.totals.belowCostLines}</dd>
                </div>
                <div className="fact">
                  <dt>{t('creditsByReason')}</dt>
                  <dd>{formatMoney(r.totals.creditsAmount, intlLocale)}</dd>
                </div>
                <div className="fact">
                  <dt>{t('shrinkByCause')}</dt>
                  <dd>
                    {formatQty(r.totals.shrinkPacks)} {t('packs')}
                    {r.totals.shrinkCost !== null ? ` · ${formatMoney(r.totals.shrinkCost, intlLocale)}${r.totals.shrinkCostPartial ? '+' : ''}` : ''}
                  </dd>
                </div>
                <div className="fact">
                  <dt>{t('voidedInvoices')}</dt>
                  <dd>
                    {r.voided.count}
                    {r.voided.count ? ` · ${formatMoney(r.voided.amount, intlLocale)}` : ''}
                  </dd>
                </div>
                <div className="fact">
                  <dt>{t('cancelledOrders')}</dt>
                  <dd>{r.cancelledOrders}</dd>
                </div>
                <div className="fact">
                  <dt>{t('unpricedLinesLabel')}</dt>
                  <dd>{r.unpricedLines}</dd>
                </div>
              </dl>
            </section>

            <section className="card stack-sm">
              <h2>{t('pricingExceptions')}</h2>
              {r.pricing.length === 0 ? (
                <Empty>{t('noResults')}</Empty>
              ) : (
                <ul className="list" data-testid="pricing">
                  {r.pricing.map((l) => (
                    <li key={`${l.invoiceId}-${l.itemId}-${l.qty}-${l.unitPrice}`} className="row">
                      <span className="row-main">
                        <Link to={`/invoices/${l.invoiceId}`} className="row-title">
                          {l.customerName} · {l.itemName}
                        </Link>
                        <span className="row-sub">
                          {t('invoice')} {l.invoiceNo} · {formatDate(l.date, intlLocale, 'date')} · {formatQty(l.qty)} × {formatMoney(l.unitPrice, intlLocale)}
                        </span>
                        <span className="row-sub">
                          {l.belowCost ? <span className="badge badge-danger">{t('belowCostBadge')}</span> : null}
                          {l.underFloor ? <span className="badge badge-danger">{t('underFloorBadge')}</span> : null}
                          {l.underTier && !l.underFloor ? <span className="badge badge-warn">{t('underTierBadge')}</span> : null}
                          {l.floorPrice !== null ? ` ${t('floor')} ${formatMoney(l.floorPrice, intlLocale)}` : ''}
                          {l.tierPrice !== null ? ` · ${t('tier')} ${formatMoney(l.tierPrice, intlLocale)}` : ''}
                        </span>
                      </span>
                      <span className="row-end">
                        <span className="row-end-label">{t('leftOnTable')}</span>
                        <span className="row-end-value owed">{formatMoney(l.gap, intlLocale)}</span>
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <div className="two-up">
              <section className="card stack-sm">
                <h2>{t('creditsByReason')}</h2>
                {r.credits.length === 0 ? (
                  <Empty>{t('noResults')}</Empty>
                ) : (
                  <dl className="facts plain" data-testid="credits">
                    {r.credits.map((c) => (
                      <div key={c.reason} className="fact">
                        <dt>
                          {t(`reason_${c.reason}` as 'reason_other')} · {c.count}
                        </dt>
                        <dd>{formatMoney(c.amount, intlLocale)}</dd>
                      </div>
                    ))}
                  </dl>
                )}
              </section>
              <section className="card stack-sm">
                <h2>{t('shrinkByCause')}</h2>
                {r.shrink.length === 0 ? (
                  <Empty>{t('noResults')}</Empty>
                ) : (
                  <dl className="facts plain" data-testid="shrink">
                    {r.shrink.map((s) => (
                      <div key={s.reason} className="fact">
                        <dt>{t(`shrink_${s.reason}` as 'shrink_other')}</dt>
                        <dd>
                          {formatQty(s.packs)} {t('packs')} · {s.cost === null ? t('noCostOnShrink') : `${formatMoney(s.cost, intlLocale)}${s.partial ? '+' : ''}`}
                        </dd>
                      </div>
                    ))}
                  </dl>
                )}
              </section>
            </div>
          </>
        )}
      </QueryView>
    </div>
  );
}
